/**
 * P1.1: TreatX Admin Email Templates Page (Dictionary-Driven)
 * Manage email notification templates driven by Email Event Dictionary
 * P2.3b: Full i18n coverage
 * 
 * BACKEND-READY ARCHITECTURE:
 * - Templates driven by immutable event keys from dictionary
 * - Admins can only edit content (subject/body), not event keys
 * - Supports audience filtering (Clinic, Patient, Internal)
 * 
 * MOCKED - No real email sending. Templates stored in localStorage.
 */
import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { 
  useEmailTemplates, 
  TEMPLATE_VARIABLES,
  EMAIL_AUDIENCE,
  EmailTemplateProvider 
} from '../../context/EmailTemplateContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Mail, ArrowLeft, LogOut, Search, Edit2, Eye, RotateCcw,
  Check, X, AlertTriangle, Send, FileText, Clock, User,
  Sparkles, Info, Copy, CheckCircle, ChevronRight, Settings,
  Building2, Users, Shield, Key, CreditCard, UserCheck,
  MessageSquare, Package, Lock
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import api from '../../utils/api';
import TemplateInlineIndicators from '../../components/admin/TemplateInlineIndicators';
import useTemplateStatusMap from '../../hooks/useTemplateStatusMap';

// Category icons and colors (driven by dictionary categories)
const CATEGORY_CONFIG = {
  application: { icon: FileText, color: 'bg-blue-100 text-blue-700', label: 'Application' },
  trial: { icon: Sparkles, color: 'bg-teal-100 text-teal-700', label: 'Trial' },
  subscription: { icon: Package, color: 'bg-indigo-100 text-indigo-700', label: 'Subscription' },
  auth: { icon: Key, color: 'bg-purple-100 text-purple-700', label: 'Authentication' },
  intake: { icon: MessageSquare, color: 'bg-cyan-100 text-cyan-700', label: 'Intake' },
  matching: { icon: UserCheck, color: 'bg-emerald-100 text-emerald-700', label: 'Matching' },
  offer: { icon: CreditCard, color: 'bg-amber-100 text-amber-700', label: 'Offers' },
  payment: { icon: CreditCard, color: 'bg-green-100 text-green-700', label: 'Payments' },
  internal: { icon: Shield, color: 'bg-gray-100 text-gray-700', label: 'Internal' },
};

// Audience icons and colors
const AUDIENCE_CONFIG = {
  [EMAIL_AUDIENCE.CLINIC]: { icon: Building2, color: 'text-purple-600', bgColor: 'bg-purple-50', label: 'Clinic' },
  [EMAIL_AUDIENCE.PATIENT]: { icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Patient' },
  [EMAIL_AUDIENCE.INTERNAL]: { icon: Shield, color: 'text-gray-600', bgColor: 'bg-gray-50', label: 'Internal' },
};

// Template Editor Modal
const TemplateEditorModal = ({ template, onClose, onSave }) => {
  const { generatePreview, getUsedVariables, resetToDefault } = useEmailTemplates();
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // TREATX-EMAIL-RENDER-PARITY-02: derive the Sample-Preview From
  // identity from the SAME canonical policy the real send uses.
  const [senderIdentity, setSenderIdentity] = useState('TreatX <notifications@treatx.health>');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/sender-identity`, { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.display) setSenderIdentity(j.display);
      } catch { /* silent — fallback keeps the correct default */ }
    })();
    return () => { cancelled = true; };
  }, []);
  
  const usedVariables = getUsedVariables({ subject, body });
  const preview = generatePreview({ subject, body });
  const categoryConfig = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.application;
  const CategoryIcon = categoryConfig.icon;
  const audienceConfig = AUDIENCE_CONFIG[template.audience];
  
  // Get available variables for this template's audience
  const availableVariables = Object.entries(TEMPLATE_VARIABLES)
    .filter(([_, config]) => config.scope?.includes(template.audience) || !config.scope)
    .map(([key, config]) => ({ key, ...config }));
  
  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onSave(template.id, { subject, body });
    setIsSaving(false);
    toast({ title: 'Template Saved', description: 'Your changes have been saved.' });
    onClose();
  };
  
  const handleReset = () => {
    resetToDefault(template.id);
    toast({ title: 'Template Reset', description: 'Template has been reset to default.' });
    onClose();
  };
  
  const insertVariable = (varName) => {
    const textArea = document.getElementById('template-body');
    const cursorPos = textArea?.selectionStart || body.length;
    const newBody = body.slice(0, cursorPos) + `{{${varName}}}` + body.slice(cursorPos);
    setBody(newBody);
  };
  
  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    toast({ title: 'Copied!', description: `{{${varName}}} copied to clipboard` });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 text-white p-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CategoryIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold">Edit: {template.name}</h2>
                  <Badge className={`${audienceConfig.bgColor} ${audienceConfig.color} text-xs`}>
                    {audienceConfig.label}
                  </Badge>
                </div>
                <p className="text-purple-200 text-sm">{template.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid="template-editor-backend-badge">BACKEND-CONNECTED</Badge>
              {!template.isUserEditable && (
                <Badge className="bg-gray-500 text-white flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Read-Only
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          {/* Event Key Badge */}
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-purple-300">Event Key:</span>
            <code className="text-xs bg-white/20 px-2 py-0.5 rounded font-mono">
              {template.eventKey}
            </code>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Editor Panel */}
          <div className={`flex-1 overflow-y-auto p-6 ${showPreview ? 'border-r' : ''}`}>
            {!template.isUserEditable ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <Lock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-700 mb-2">Internal Template</h4>
                <p className="text-sm text-gray-500">
                  This template is for internal operations and cannot be edited.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject
                  </label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    className="font-medium"
                    data-testid="template-subject-input"
                  />
                </div>

                {/* Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Body
                  </label>
                  <Textarea
                    id="template-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Enter email body..."
                    rows={16}
                    className="font-mono text-sm"
                    data-testid="template-body-input"
                  />
                </div>

                {/* Variables Reference */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Available Variables for {audienceConfig.label} Templates
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {availableVariables.map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => insertVariable(key)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                          usedVariables.includes(key)
                            ? 'bg-green-100 border-green-300 text-green-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
                        }`}
                        title={`${label}`}
                      >
                        {`{{${key}}}`}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Green = used in template. Click to insert at cursor position.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="w-[450px] overflow-y-auto bg-slate-50 p-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Preview (with mock data)
              </h4>
              
              {/* Email Preview Card */}
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                {/* Email Header */}
                <div className="bg-gray-100 p-3 border-b">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <span className="font-medium">From:</span>
                    <span data-testid="email-preview-from">{senderIdentity}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <span className="font-medium">To:</span>
                    <span>
                      {template.audience === EMAIL_AUDIENCE.CLINIC 
                        ? TEMPLATE_VARIABLES.clinicEmail?.example
                        : template.audience === EMAIL_AUDIENCE.PATIENT
                          ? TEMPLATE_VARIABLES.patientEmail?.example
                          : 'ops@treatx.health'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-600">Subject:</span>
                    <span className="font-semibold text-gray-900">{preview.subject}</span>
                  </div>
                </div>
                
                {/* Email Body */}
                <div className="p-4">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                    {preview.body}
                  </pre>
                </div>
              </div>

              {/* Variables Used */}
              <div className="mt-4 p-3 bg-white rounded-lg border">
                <p className="text-xs font-medium text-gray-500 mb-2">Variables in this template:</p>
                <div className="flex flex-wrap gap-1">
                  {usedVariables.map(varName => (
                    <Badge 
                      key={varName} 
                      variant="outline" 
                      className="text-xs cursor-pointer hover:bg-gray-100"
                      onClick={() => copyVariable(varName)}
                    >
                      {TEMPLATE_VARIABLES[varName]?.label || varName}
                      <Copy className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            {template.isUserEditable && (
              <Button
                variant="outline"
                onClick={handleReset}
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                disabled={template.isDefault}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Default
              </Button>
            )}
            {template.lastModified && (
              <span className="text-xs text-gray-500">
                Last modified: {formatDateLocale(template.lastModified)}
                {template.modifiedBy && ` by ${template.modifiedBy}`}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {template.isUserEditable && (
              <Button 
                onClick={handleSave}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={isSaving}
                data-testid="save-template-btn"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Modal
const PreviewModal = ({ template, onClose }) => {
  const { generatePreview } = useEmailTemplates();
  const preview = generatePreview(template);
  const categoryConfig = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.application;
  const CategoryIcon = categoryConfig.icon;
  const audienceConfig = AUDIENCE_CONFIG[template.audience];

  // TREATX-ADMIN-TEMPLATE-ACTIVATION-04: admin test-send hook
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [senderIdentity, setSenderIdentity] = useState('TreatX <notifications@treatx.health>');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/sender-identity`, { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.display) setSenderIdentity(j.display);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast({ title: 'Invalid email', description: 'Enter a valid recipient address.', variant: 'destructive' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      // Build a variables dict from the dictionary example values for
      // every variable the template references. Keeps the test send
      // pipeline-identical to live sends (same resolver, same provider).
      const vars = {};
      Object.entries(TEMPLATE_VARIABLES).forEach(([k, cfg]) => {
        if (
          (template.subject && template.subject.includes(`{{${k}}}`)) ||
          (template.body && template.body.includes(`{{${k}}}`))
        ) {
          vars[k] = cfg.example;
        }
      });
      const res = await api.post('/email-templates/test-send', {
        event_key: template.eventKey,
        to: testEmail,
        variables: vars,
        locale: 'en',
      });
      setTestResult(res.data);
      toast({
        title: 'Test send dispatched',
        description: `provider=${res.data.provider} • source=${res.data.template_source}`,
      });
    } catch (err) {
      const detail = err?.response?.data?.message || err?.message || 'Unknown error';
      setTestResult({ error: detail });
      toast({ title: 'Test send failed', description: detail, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`p-5 flex items-center justify-between ${categoryConfig.color}`}>
          <div className="flex items-center gap-3">
            <CategoryIcon className="h-5 w-5" />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold">{template.name}</h2>
                <Badge variant="outline" className="text-xs bg-white/50">
                  {audienceConfig.label}
                </Badge>
              </div>
              <p className="text-sm opacity-80">{template.description}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {/* Email Header */}
            <div className="bg-gray-100 p-3 border-b">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <span className="font-medium">From:</span>
                <span data-testid="email-preview-from-testsend">{senderIdentity}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <span className="font-medium">To:</span>
                <span>
                  {template.audience === EMAIL_AUDIENCE.CLINIC 
                    ? TEMPLATE_VARIABLES.clinicEmail?.example
                    : template.audience === EMAIL_AUDIENCE.PATIENT
                      ? TEMPLATE_VARIABLES.patientEmail?.example
                      : 'ops@treatx.health'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-600">Subject:</span>
                <span className="font-semibold text-gray-900">{preview.subject}</span>
              </div>
            </div>
            
            {/* Email Body */}
            <div className="p-4">
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                {preview.body}
              </pre>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2" data-testid="template-preview-mode-note">
            <Info className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-slate-700">
              <strong>Sample preview</strong> — variables are filled with example values from the dictionary
              (e.g. <code className="px-1 bg-slate-100 rounded">&lt;sample-verification-token&gt;</code>). For a
              real render against your inbox use <strong>Send test email</strong> below; it goes through the
              canonical NotificationService and delivers via Zoho SMTP.
            </p>
          </div>

          {/* Test-send affordance (TREATX-ADMIN-TEMPLATE-ACTIVATION-04) */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`test-send-block-${template.eventKey}`}>
            <div className="flex items-center gap-2 mb-2 text-sm text-blue-900">
              <Send className="h-4 w-4" />
              <span className="font-semibold">Admin test send</span>
              <span className="text-blue-700">— dispatches via canonical NotificationService (transport is environment-driven)</span>
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1 bg-white"
                data-testid={`test-send-email-${template.eventKey}`}
              />
              <Button
                onClick={handleTestSend}
                disabled={testing}
                data-testid={`test-send-button-${template.eventKey}`}
              >
                {testing ? 'Sending…' : 'Send test'}
              </Button>
            </div>
            {testResult && !testResult.error && (
              <div className="mt-3 text-xs space-y-1 text-blue-900" data-testid={`test-send-result-${template.eventKey}`}>
                <div><strong>Provider:</strong> {testResult.provider}</div>
                <div><strong>Status:</strong> {testResult.provider_status}</div>
                <div><strong>Template source:</strong> <code className="bg-white px-1 rounded">{testResult.template_source}</code></div>
                <div><strong>Locale:</strong> {testResult.locale}</div>
                {testResult.missing_variables && testResult.missing_variables.length > 0 && (
                  <div className="text-amber-700"><strong>Missing variables:</strong> {testResult.missing_variables.join(', ')}</div>
                )}
                {testResult.provider_error && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800" data-testid={`test-send-provider-error-${template.eventKey}`}>
                    <div className="font-semibold mb-1">Provider error</div>
                    <div className="font-mono whitespace-pre-wrap break-words">{testResult.provider_error}</div>
                    <div className="mt-1 text-red-700">The template rendered correctly and the request reached the email provider, but the provider refused to accept the message. See the error above — this is typically a mailbox/rate/reputation block at the provider (Zoho) and needs to be resolved in the provider console.</div>
                  </div>
                )}
                {testResult.skipped_reason && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800" data-testid={`test-send-skipped-${template.eventKey}`}>
                    <div className="font-semibold">Skipped</div>
                    <div>{testResult.skipped_reason}</div>
                  </div>
                )}
                <details className="mt-1">
                  <summary className="cursor-pointer text-blue-700">View rendered output</summary>
                  <div className="mt-2 p-2 bg-white border rounded">
                    <div className="text-xs text-gray-500 mb-1">Subject:</div>
                    <div className="font-medium mb-2">{testResult.rendered_subject}</div>
                    <div className="text-xs text-gray-500 mb-1">Body:</div>
                    <pre className="whitespace-pre-wrap text-xs">{testResult.rendered_html}</pre>
                  </div>
                </details>
              </div>
            )}
            {testResult && testResult.error && (
              <div className="mt-2 text-xs text-red-700">{testResult.error}</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end">
          <Button onClick={onClose}>Close Preview</Button>
        </div>
      </div>
    </div>
  );
};

// Main Content Component
const TreatXAdminEmailTemplatesContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin, logout } = useTreatXAdminAuth();
  const { 
    templates, 
    getTemplatesByAudience, 
    getEditableTemplates,
    toggleTemplateStatus, 
    updateTemplate,
    getStatistics,
    // TREATX-OWNER-UAT-EMAIL-EN-TR-01 — locale toggle wiring.
    selectedLocale,
    setSelectedLocale,
  } = useEmailTemplates();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeAudience, setActiveAudience] = useState('all');

  // 11C: live per-template status from canonical
  // /admin/notifications/template-status endpoint (read-only).
  const { statusByEventKey } = useTemplateStatusMap();

  // Redirect if not logged in
  useEffect(() => {
    if (!admin) {
      navigate('/treatx-admin/login');
    }
  }, [admin, navigate]);

  if (!admin) return null;

  // Get statistics
  const stats = getStatistics();

  // Filter templates by audience and search
  const getFilteredTemplates = () => {
    let filtered = activeAudience === 'all' 
      ? getEditableTemplates() // Only show editable templates (excludes internal)
      : getTemplatesByAudience(activeAudience);
    
    if (searchQuery) {
      filtered = filtered.filter(tpl => 
        tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.eventKey.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  const filteredTemplates = getFilteredTemplates();

  // Group templates by category for display
  const groupedTemplates = filteredTemplates.reduce((acc, tpl) => {
    const cat = tpl.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tpl);
    return acc;
  }, {});

  const handleLogout = () => {
    logout();
    navigate('/treatx-admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-800 to-pink-700 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/treatx-admin/dashboard')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="email-templates-title">
                  Email Templates
                </h1>
                <p className="text-sm text-purple-200">Dictionary-Driven Template Management</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 ml-2" data-testid="email-templates-header-backend-badge">BACKEND-CONNECTED</Badge>
            </div>
            <div className="flex items-center gap-4">
              {/* TREATX-OWNER-UAT-EMAIL-EN-TR-01 (2026-08): canonical
                  `email_templates` stores one row per (event_key, locale);
                  this toggle drives both fetch (?locale=) and save
                  (body.locale) — no parallel translation store. */}
              <div
                className="inline-flex items-center rounded-lg border border-white/30 overflow-hidden"
                role="tablist"
                aria-label="Template locale"
                data-testid="email-templates-locale-toggle"
              >
                {['en', 'tr'].map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    role="tab"
                    aria-selected={selectedLocale === loc}
                    data-testid={`email-templates-locale-${loc}`}
                    onClick={() => setSelectedLocale(loc)}
                    className={
                      selectedLocale === loc
                        ? 'px-3 py-1 text-xs font-semibold bg-white text-purple-700'
                        : 'px-3 py-1 text-xs font-semibold text-purple-100 hover:bg-white/10'
                    }
                  >
                    {loc.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <img 
                  src={admin.avatar} 
                  alt={admin.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{admin.name}</p>
                  <p className="text-xs text-purple-200">{admin.title}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-purple-200 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Info Banner — backend-connected as of TREATX-PRODUCTION-P0-OWNER-ACCEPTANCE-02 §P0-3 */}
      <div className="bg-emerald-50 border-b border-emerald-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-emerald-900">
            <CheckCircle className="h-4 w-4 text-emerald-700" />
            <p className="text-sm">
              <strong>Backend-connected.</strong> Templates are persisted in MongoDB
              (<code className="mx-1 px-1 bg-emerald-100 rounded">email_templates</code> collection); event keys
              are immutable, only subject/body can be edited. Outbound delivery goes through the canonical
              <code className="mx-1 px-1 bg-emerald-100 rounded">NotificationService</code> and uses your
              configured SMTP provider (Zoho). The in-page <em>preview</em> still renders client-side with
              sample variable values — use <strong>Send test email</strong> on any template for a real
              end-to-end send.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-700">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Events</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.byAudience[EMAIL_AUDIENCE.CLINIC]}</p>
              <p className="text-xs text-slate-500">Clinic</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.byAudience[EMAIL_AUDIENCE.PATIENT]}</p>
              <p className="text-xs text-slate-500">Patient</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              <p className="text-xs text-slate-500">Active</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.customized}</p>
              <p className="text-xs text-slate-500">Customized</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Quick Access Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setActiveAudience(EMAIL_AUDIENCE.CLINIC)}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">Clinic Templates</h3>
                <p className="text-sm text-purple-600">{stats.byAudience[EMAIL_AUDIENCE.CLINIC]} events • Application, Trial, Subscription</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate('/treatx-admin/patient-email-templates')}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Patient Templates</h3>
                  <p className="text-sm text-blue-600">{stats.byAudience[EMAIL_AUDIENCE.PATIENT]} events • Auth, Intake, Matching, Offers</p>
                </div>
              </div>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="open-patient-templates-btn">
                Open →
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Notification Templates
                </CardTitle>
                <CardDescription>
                  Manage email templates by audience type. Event keys are immutable.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                {/* Audience Filter Tabs */}
                <div className="flex bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setActiveAudience('all')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      activeAudience === 'all' 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setActiveAudience(EMAIL_AUDIENCE.CLINIC)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                      activeAudience === EMAIL_AUDIENCE.CLINIC 
                        ? 'bg-white text-purple-600 shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    Clinic
                  </button>
                  <button
                    onClick={() => setActiveAudience(EMAIL_AUDIENCE.PATIENT)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
                      activeAudience === EMAIL_AUDIENCE.PATIENT 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Users className="h-3.5 w-3.5" />
                    Patient
                  </button>
                </div>
                
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                    data-testid="search-templates-input"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => {
              const catConfig = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.application;
              const CatIcon = catConfig.icon;
              
              return (
                <div key={category} className="mb-6 last:mb-0">
                  {/* Category Header */}
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <div className={`p-1.5 rounded ${catConfig.color}`}>
                      <CatIcon className="h-4 w-4" />
                    </div>
                    <h3 className="font-semibold text-gray-700">{catConfig.label}</h3>
                    <Badge variant="outline" className="text-xs">
                      {categoryTemplates.length} templates
                    </Badge>
                  </div>
                  
                  {/* Templates in Category */}
                  <div className="space-y-2">
                    {categoryTemplates.map(template => {
                      const audienceConfig = AUDIENCE_CONFIG[template.audience];
                      const AudienceIcon = audienceConfig.icon;
                      
                      return (
                        <div 
                          key={template.id}
                          className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                            template.isActive ? 'bg-white' : 'bg-gray-50 opacity-75'
                          }`}
                          data-testid={`template-${template.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1">
                              {/* Audience Icon */}
                              <div className={`p-2 rounded-lg ${audienceConfig.bgColor}`}>
                                <AudienceIcon className={`h-5 w-5 ${audienceConfig.color}`} />
                              </div>
                              
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                  {!template.isDefault && (
                                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                      Customized
                                    </Badge>
                                  )}
                                  {!template.isActive && (
                                    <Badge variant="outline" className="text-xs bg-gray-100 text-gray-500">
                                      Disabled
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{template.description}</p>
                                <code className="text-xs text-gray-400 font-mono mt-1 block">
                                  {template.eventKey}
                                </code>
                                {/* 11C inline status indicators */}
                                <TemplateInlineIndicators
                                  eventKey={template.eventKey}
                                  isActive={template.isActive}
                                  subject={template.subject}
                                  body={template.body}
                                  allowedVariables={template.defaultVariables}
                                  serverStatus={statusByEventKey[template.eventKey] || null}
                                />
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-3 ml-4">
                              {/* Active Toggle */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {template.isActive ? 'Active' : 'Disabled'}
                                </span>
                                <Switch
                                  checked={template.isActive}
                                  onCheckedChange={() => toggleTemplateStatus(template.id)}
                                  data-testid={`toggle-${template.id}`}
                                />
                              </div>
                              
                              {/* View Button */}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setPreviewTemplate(template)}
                                data-testid={`preview-${template.id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              
                              {/* Edit Button */}
                              <Button 
                                size="sm"
                                onClick={() => setSelectedTemplate(template)}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid={`edit-${template.id}`}
                              >
                                <Edit2 className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            
            {filteredTemplates.length === 0 && (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No templates match your search</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Info className="h-4 w-4" />
            Event Dictionary Structure
          </h4>
          <p className="text-sm text-blue-700 mb-3">
            Templates are driven by immutable event keys. Admins can only edit content (subject/body), not create new events.
          </p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="h-4 w-4 text-purple-600" />
                <span className="font-medium text-gray-700">Clinic Events</span>
              </div>
              <p className="text-xs text-gray-500">Application, Trial, Subscription lifecycle</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-gray-700">Patient Events</span>
              </div>
              <p className="text-xs text-gray-500">Auth, Intake, Matching, Offers, Payments</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4 text-gray-600" />
                <span className="font-medium text-gray-700">Internal Events</span>
              </div>
              <p className="text-xs text-gray-500">Admin alerts, Sales notifications (read-only)</p>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {selectedTemplate && (
        <TemplateEditorModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSave={updateTemplate}
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};

// Wrapper with provider
const TreatXAdminEmailTemplates = () => {
  return (
    <EmailTemplateProvider>
      <TreatXAdminEmailTemplatesContent />
    </EmailTemplateProvider>
  );
};

export default TreatXAdminEmailTemplates;
